import React, { useState, useEffect } from 'react';
import { Plus, KeyRound, UserX, UserCheck, ShieldCheck, Users as UsersIcon, Trash2, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api, { getCompanies } from '../lib/api';

const ROLES = [
  { value: 'sales', label: 'Sales Person (limited access)' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'operations', label: 'Operations' },
  { value: 'operation_manager', label: 'Operation Manager' },
  { value: 'operation_executive', label: 'Operation Executive' },
  { value: 'operation_coordinator', label: 'Operation Coordinator' },
  { value: 'finance', label: 'Finance' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'procurement_manager', label: 'Procurement Manager' },
  { value: 'admin', label: 'Admin' },
];

const emptyForm = {
  full_name: '',
  email: '',
  password: '',
  role: 'sales',
  company_ids: [],
};

export const UsersManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newPassword, setNewPassword] = useState('');

  const isAdmin = currentUser?.role === 'admin';

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setCompanies(res.data || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadCompanies();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <ShieldCheck className="mx-auto h-16 w-16 text-red-500 mb-3" />
        <h2 className="text-2xl font-bold">Admin Only</h2>
        <p className="text-muted-foreground">You do not have permission to manage users.</p>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/admin/users', form);
      toast.success(`User created. Share login: ${form.email} / ${form.password}`);
      setCreateOpen(false);
      setForm(emptyForm);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      if (u.is_active) {
        await api.delete(`/admin/users/${u.id}`);
        toast.success('User deactivated');
      } else {
        await api.patch(`/admin/users/${u.id}`, { is_active: true });
        toast.success('User reactivated');
      }
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const handlePermanentDelete = async (u) => {
    const confirmText = `⚠️ PERMANENTLY delete ${u.email}?\n\nThis cannot be undone. The user will be completely removed from the database, but the action will be recorded in the System Audit Log.\n\nType YES in the next prompt to confirm.`;
    if (!window.confirm(confirmText)) return;
    const typed = window.prompt('Type YES (in capitals) to permanently delete this user:');
    if (typed !== 'YES') { toast.info('Deletion cancelled'); return; }
    try {
      await api.delete(`/admin/users/${u.id}?hard=true`);
      toast.success('User permanently deleted');
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const openAuditLog = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await api.get('/admin/users/audit-log?limit=200');
      setAuditItems(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const openPasswordReset = (u) => {
    setSelected(u);
    setNewPassword('');
    setPwdOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/admin/users/${selected.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset. Share new login: ${selected.email} / ${newPassword}`);
      setPwdOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      u.email?.toLowerCase().includes(q) ||
      u.full_name?.toLowerCase().includes(q);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const roleColor = (role) => ({
    admin: 'bg-red-100 text-red-700',
    sales_manager: 'bg-purple-100 text-purple-700',
    sales: 'bg-blue-100 text-blue-700',
    operations: 'bg-amber-100 text-amber-700',
    finance: 'bg-emerald-100 text-emerald-700',
    procurement: 'bg-slate-100 text-slate-700',
  }[role] || 'bg-slate-100 text-slate-700');

  return (
    <div className="space-y-6" data-testid="users-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <UsersIcon className="h-8 w-8" /> User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage login credentials for sales personnel and other staff.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openAuditLog} data-testid="audit-log-button">
            <History className="h-4 w-4 mr-2" /> System Audit Log
          </Button>
          <Button onClick={() => { setForm(emptyForm); setCreateOpen(true); }} data-testid="new-user-button">
            <Plus className="h-4 w-4 mr-2" /> New User
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <Input
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="user-search-input"
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-56" data-testid="user-role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No users found.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{u.full_name}</p>
                      <Badge className={roleColor(u.role)}>{u.role}</Badge>
                      {!u.is_active && <Badge variant="destructive">Deactivated</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{u.email}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Created: {u.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPasswordReset(u)}
                      data-testid={`reset-password-${u.email}`}
                    >
                      <KeyRound className="h-4 w-4 mr-1" /> Reset password
                    </Button>
                    <Button
                      variant={u.is_active ? 'outline' : 'default'}
                      size="sm"
                      onClick={() => handleToggleActive(u)}
                      disabled={u.id === currentUser?.id}
                      data-testid={`toggle-active-${u.email}`}
                    >
                      {u.is_active ? <><UserX className="h-4 w-4 mr-1" /> Deactivate</> : <><UserCheck className="h-4 w-4 mr-1" /> Reactivate</>}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePermanentDelete(u)}
                      disabled={u.id === currentUser?.id}
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      data-testid={`delete-user-${u.email}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Set login credentials. Share the email and password with the person.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full name</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="new-user-fullname"
              />
            </div>
            <div>
              <Label>Email (login)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label>Password (min 6 chars)</Label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Share this with the user"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Operating Company (optional)</Label>
              <Select
                value={form.company_ids[0] || ''}
                onValueChange={(v) => setForm({ ...form, company_ids: v ? [v] : [] })}
              >
                <SelectTrigger data-testid="new-user-company"><SelectValue placeholder="Any / not restricted" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="submit-new-user">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selected?.email}</strong>. Share this password with the user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>New password (min 6 chars)</Label>
            <Input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} data-testid="submit-password-reset">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* System Audit Log dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="audit-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> System Audit Log
            </DialogTitle>
            <DialogDescription>
              Immutable record of every user create / update / (de)activate / delete action performed by admins.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2">
            {auditLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : auditItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No audit entries yet.</div>
            ) : (
              <div className="divide-y">
                {auditItems.map((a) => (
                  <div key={a.id} className="py-2.5 text-sm" data-testid={`audit-row-${a.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={
                        a.action === 'user_deleted' ? 'bg-red-100 text-red-800'
                          : a.action === 'user_deactivated' ? 'bg-orange-100 text-orange-800'
                          : a.action === 'user_created' ? 'bg-emerald-100 text-emerald-800'
                          : a.action === 'user_reactivated' ? 'bg-blue-100 text-blue-800'
                          : 'bg-slate-100 text-slate-800'
                      }>
                        {a.action.replaceAll('_', ' ')}
                      </Badge>
                      <span className="font-medium">{a.target_user_email}</span>
                      <span className="text-xs text-muted-foreground">
                        by <b>{a.admin_email}</b>
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(a.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {a.extra && Object.keys(a.extra).length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {JSON.stringify(a.extra)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagementPage;
